import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { Menu, X, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

const navLinks = [
  { label: 'Recursos', href: '/features' },
  { label: 'Preços', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  {
    label: 'Empresa',
    children: [
      { label: 'Sobre nós', href: '/about' },
      { label: 'Contato', href: '/contact' },
    ],
  },
];

// Scroll para o topo ao mudar de rota
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const LandingLayout = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <ScrollToTop />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-950/90 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">MyBank</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) =>
                link.children ? (
                  // Wrapper com handlers de hover — cobre o botão E o dropdown,
                  // eliminando o "gap" que fazia o menu sumir ao mover o mouse
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(link.label)}
                    onMouseLeave={() => setDropdownOpen(null)}
                  >
                    <button
                      className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium"
                    >
                      {link.label}
                      <ChevronDown size={14} className={clsx('transition-transform', dropdownOpen === link.label && 'rotate-180')} />
                    </button>
                    {dropdownOpen === link.label && (
                      <div className="absolute top-full left-0 pt-1 min-w-[160px]">
                        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 overflow-hidden">
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-primary-600 transition-colors"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={clsx(
                      'px-3 py-2 text-sm rounded-lg transition-colors font-medium',
                      location.pathname === link.href
                        ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Auth buttons */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Link
                  to="/app/dashboard"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm"
                >
                  Acessar App
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="hidden sm:block text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white font-medium px-3 py-2"
                  >
                    Entrar
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm shadow-sm"
                  >
                    Começar Grátis
                  </Link>
                </>
              )}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 py-4 px-4">
            <nav className="space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">{link.label}</p>
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => setMobileOpen(false)}
                        className="block px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    {link.label}
                  </Link>
                )
              )}
              {!isAuthenticated && (
                <div className="pt-2 border-t border-gray-100 dark:border-gray-800 mt-2">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">Entrar</Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-primary-600">Começar Grátis</Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="text-xl font-bold text-white">MyBank</span>
              </Link>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Mais que um app financeiro. Seu consultor financeiro particular, disponível 24h por dia.
              </p>
              <div className="flex gap-3 mt-5">
                <a href="#" className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors text-xs font-bold text-white">in</a>
                <a href="#" className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors text-xs font-bold text-white">ig</a>
                <a href="#" className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors text-xs font-bold text-white">tw</a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Produto</h4>
              <ul className="space-y-2.5">
                <li><Link to="/features" className="text-sm hover:text-primary-400 transition-colors">Recursos</Link></li>
                <li><Link to="/pricing" className="text-sm hover:text-primary-400 transition-colors">Preços</Link></li>
                <li><Link to="/blog" className="text-sm hover:text-primary-400 transition-colors">Blog</Link></li>
                <li><Link to="/help" className="text-sm hover:text-primary-400 transition-colors">Central de Ajuda</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Empresa</h4>
              <ul className="space-y-2.5">
                <li><Link to="/about" className="text-sm hover:text-primary-400 transition-colors">Sobre nós</Link></li>
                <li><Link to="/contact" className="text-sm hover:text-primary-400 transition-colors">Contato</Link></li>
                <li><a href="mailto:suporte@mybank.com.br" className="text-sm hover:text-primary-400 transition-colors">suporte@mybank.com.br</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Legal</h4>
              <ul className="space-y-2.5">
                <li><Link to="/privacy" className="text-sm hover:text-primary-400 transition-colors">Privacidade</Link></li>
                <li><Link to="/terms" className="text-sm hover:text-primary-400 transition-colors">Termos de Uso</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">© {new Date().getFullYear()} MyBank. Todos os direitos reservados.</p>
            <p className="text-xs text-gray-600">Feito com dedicação no Brasil 🇧🇷</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingLayout;
